import { Routes, Route } from 'react-router-dom';
import { lazy, Suspense, useState } from 'react';
import Loading from './components/Loding/Loading';
import axios from 'axios';
import { useAuthContext } from './context/subContexts/AuthContext';
import { useCheckToken } from './hooks/useCheckToken';
import ComprehensiveLawHtml from './components/ComprehensiveRules/ComprehensiveLawHtml';

// import SectionsAndChapters from './components/ComprehensiveRules/SectionsAndChapters/SectionsAndChapters';
// import ProposeANewLaw from './components/ProposalsAndBills/ProposeANewLaw';

// Rate limiting configuration
const axiosInstance = axios.create();
axiosInstance.interceptors.response.use(null, async (error) => {
  if (error.response?.status === 429) {
    const retryAfter = error.response.headers['retry-after'] || 5;
    await new Promise((resolve) => setTimeout(resolve, retryAfter * 1000));
    return axiosInstance(error.config);
  }
  return Promise.reject(error);
});

// Implement retry queue
const retryQueue = [];
let isRetrying = false;

const processQueue = async () => {
  if (isRetrying) return;
  isRetrying = true;

  while (retryQueue.length) {
    const request = retryQueue.shift();
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay between requests
      await request();
    } catch (error) {
      console.error('Retry failed:', error);
    }
  }

  isRetrying = false;
};

const lazyLoadWithRetry = (importFn, retries = 2, interval = 1000) => {
  return lazy(() => {
    const attempt = (n) =>
      importFn().catch(async (error) => {
        const isChunkError =
          error?.name === 'ChunkLoadError' || /Loading chunk \d+ failed/.test(error?.message || '');
        if (isChunkError) {
          if (n <= 0) {
            if (!sessionStorage.getItem('chunk-error-reloaded')) {
              sessionStorage.setItem('chunk-error-reloaded', 'true');
              try {
                if ('caches' in window) {
                  const keys = await caches.keys();
                  await Promise.all(keys.map((k) => caches.delete(k)));
                }
              } catch {}
              window.location.reload();
            }
            throw error;
          }
          await new Promise((resolve) => setTimeout(resolve, interval));
          return attempt(n - 1);
        }
        if (error?.response?.status === 429) {
          return new Promise((resolve) => {
            retryQueue.push(() => attempt(n).then(resolve));
            processQueue();
          });
        }
        throw error;
      });
    return attempt(retries);
  });
};

// Update component imports to use retry logic
// const NewLogin = lazyLoadWithRetry(() => import('./components/Login/NewLogin'));
// const Layout = lazyLoadWithRetry(() => import('./Layout'));
// ... apply to other components
// Lazy loaded components
const NewLogin = lazyLoadWithRetry(() => import('./components/Login/NewLogin'));
const Layout = lazyLoadWithRetry(() => import('./Layout'));
const Home = lazyLoadWithRetry(() => import('./components/MainPage/Home'));
const AdvanceSearch = lazyLoadWithRetry(() =>
  import('./components/AdvanceSearch/AdvanceSearch')
);
const ViewText = lazyLoadWithRetry(() =>
  import('./components/ViewText/ViewText')
);
const ViewTextcopy = lazyLoadWithRetry(() =>
  import('./components/ViewText/ViewTextcopy')
);
const MapIran = lazyLoadWithRetry(() => import('./components/MapIran/MapIran'));
const RevisionList = lazyLoadWithRetry(() =>
  import('./components/RevisionList/RevisionList')
);
const ThematicList = lazyLoadWithRetry(() =>
  import('./components/ThematicList/ThematicList')
);
const ViewNews = lazyLoadWithRetry(() => import('./components/News/ViewNews'));
const ComprehensiveRules = lazyLoadWithRetry(() =>
  import('./components/ComprehensiveRules/ComprehensiveRules')
);
const ExecutiveDevices = lazyLoadWithRetry(() =>
  import('./components/ExecutiveDevices/ExecutiveDevices')
);
const ApprovalAuthorities = lazyLoadWithRetry(() =>
  import('./components/ApprovalAuthorities/ApprovalAuthorities')
);
const CreateBook = lazyLoadWithRetry(() =>
  import('./components/ComprehensiveRules/CreateBook')
);
const ProposalsAndBills = lazyLoadWithRetry(() =>
  import('./components/ProposalsAndBills/ProposalsAndBills')
);
const ProposalsAndBillsDetail = lazyLoadWithRetry(() =>
  import('./components/ProposalsAndBills/ProposalsAndBillsDetail')
);
const SectionsAndChapters = lazyLoadWithRetry(() =>
  import(
    './components/ComprehensiveRules/SectionsAndChapters/SectionsAndChapters'
  )
);
const ProposeANewLaw = lazyLoadWithRetry(() =>
  import('./components/ProposalsAndBills/ProposeANewLaw')
);

const ProposeANewLawForMemberOfParliament = lazyLoadWithRetry(() =>
  import('./components/ProposalsAndBills/ProposeANewLawForMemberOfParliament')
);

const DraftComperhensiveLaw = lazyLoadWithRetry(() =>
  import('./components/ComprehensiveRules/DraftComperhensiveLaw')
);
const Login = lazyLoadWithRetry(() => import('./components/Login/Login'));
const LawList = lazyLoadWithRetry(() =>
  import('./components/ComprehensiveRules/LawList')
);
const HelpPage = lazyLoadWithRetry(() => import('./components/News/Help'));

const AppRoutes = () => (
  <Routes>
    <Route path='login' element={<NewLogin />} />
    <Route
      path='/ComprehensiveLawHtml/:subjectId/:name/:orderbyLaw'
      element={<ComprehensiveLawHtml />}
    />
    <Route path='/' element={<Layout />}>
      <Route index element={<Home />} />
      <Route path='/AdvanceSearch' element={<AdvanceSearch />} />
      <Route path='/ViewText/:id' element={<ViewTextcopy />} />
      {/* <Route path='/ViewText/:id' element={<ViewText />} /> */}
      <Route path='/MapIran' element={<MapIran />} />
      <Route path='/RevisionList' element={<RevisionList />} />
      <Route path='/ThematicList' element={<ThematicList />} />
      <Route path='/blog/:id' element={<ViewNews />} />
      <Route path='/ComprehensiveRulesList' element={<LawList />} />
      <Route
        path='/ComprehensiveRulesList/:id/:name'
        element={<ComprehensiveRules />}
      />
      <Route
        path='/ComprehensiveRulesList/:id/'
        element={<SectionsAndChapters />}
      />
      <Route path='/ExecutiveDevices' element={<ExecutiveDevices />} />
      <Route path='/ApprovalAuthorities' element={<ApprovalAuthorities />} />
      <Route path='/ProposalsAndBills' element={<ProposalsAndBills />} />
      <Route
        path='/ProposalsAndBills/:id'
        element={<ProposalsAndBillsDetail />}
      />
      <Route path='/Proposenewlaw' element={<ProposeANewLaw />} />
      <Route
        path='/ProposeANewLawForMemberOfParliament'
        element={<ProposeANewLawForMemberOfParliament />}
      />
      <Route
        path='/DraftComprehensiveLaw/:id/:name'
        element={<DraftComperhensiveLaw />}
      /> 
      <Route
        path='/DraftComprehensiveLaw/:id/:name/:orderbyLawDraft'
        element={<DraftComperhensiveLaw />}
      />
     <Route path='/help' element={<HelpPage />} />
    </Route>
    <Route path='/Createbook' element={<CreateBook />} />
  </Routes>
);

function App() {
  const { continuee } = useAuthContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  useCheckToken();

  return (
    <Suspense fallback={<Loading />}>
      {continuee ? (
        <AppRoutes />
      ) : (
        <Login
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
        />
      )}
    </Suspense>
  );
}

export default App;
